import type { ProfileContent } from '@amorphous/core';
import type { PillMode } from './PillChoice';
import type { Theme } from '../hooks/useSettings';
import type { Notification } from '../storage/notifications';
import { Avatar } from './Avatar';
import { NotificationBell } from './NotificationBell';
import './Header.css';

interface HeaderProps {
  mode: PillMode;
  onModeToggle: () => void;
  onNewArticle: () => void;
  identityShortId: string;
  theme?: Theme;
  onThemeToggle?: () => void;
  profile?: ProfileContent;
  onProfileEdit?: () => void;
  karma?: number;
  onFriendsClick?: () => void;
  friendsCount?: number;
  onModulesClick?: () => void;
  installedModulesCount?: number;
  runningModulesCount?: number;
  onClipboardClick?: () => void;
  clipboardCount?: number;
  onAccountSettings?: () => void;
  // Notifications
  notifications?: Notification[];
  unreadNotificationCount?: number;
  onMarkNotificationRead?: (id: string) => void;
  onMarkAllNotificationsRead?: () => void;
  onDeleteNotification?: (id: string) => void;
  onClearAllNotifications?: () => void;
  onNotificationClick?: (notification: Notification) => void;
  // Messages
  onMessagesClick?: () => void;
  unreadMessagesCount?: number;
  // User Directory
  onUserDirectoryClick?: () => void;
}

export function Header({
  mode,
  onModeToggle,
  onNewArticle,
  identityShortId,
  theme,
  onThemeToggle,
  profile,
  onProfileEdit,
  karma = 0,
  onFriendsClick,
  friendsCount = 0,
  onModulesClick,
  installedModulesCount = 0,
  runningModulesCount = 0,
  onClipboardClick,
  clipboardCount = 0,
  onAccountSettings,
  notifications = [],
  unreadNotificationCount = 0,
  onMarkNotificationRead,
  onMarkAllNotificationsRead,
  onDeleteNotification,
  onClearAllNotifications,
  onNotificationClick,
  onMessagesClick,
  unreadMessagesCount = 0,
  onUserDirectoryClick,
}: HeaderProps) {
  const isRedPill = mode === 'red';

  return (
    <header className={`app-header header-${mode}`}>
      <div className="header-left">
        <h1 className="header-title">AMORPHOUS</h1>
        <button
          className={`mode-toggle ${mode}`}
          onClick={onModeToggle}
          title={`Switch to ${isRedPill ? 'Blue' : 'Red'} Pill mode`}
        >
          <span className="mode-indicator">{isRedPill ? 'O' : 'O'}</span>
          <span className="mode-label">{isRedPill ? 'RED' : 'BLUE'}</span>
        </button>
      </div>

      <div className="header-right">
        <button className="new-article-btn" onClick={onNewArticle}>
          + New Article
        </button>
        {onFriendsClick && (
          <button
            className="friends-btn"
            onClick={onFriendsClick}
            title="Friends & Blocked"
          >
            <span className="friends-icon">👥</span>
            {friendsCount > 0 && <span className="friends-count">{friendsCount}</span>}
          </button>
        )}
        {onUserDirectoryClick && (
          <button
            className="user-directory-btn"
            onClick={onUserDirectoryClick}
            title="User Directory"
          >
            <span className="user-directory-icon">🔍</span>
          </button>
        )}
        {onModulesClick && (
          <button
            className="modules-btn"
            onClick={onModulesClick}
            title="Modules"
          >
            <span className="modules-icon">📦</span>
            {runningModulesCount > 0 && (
              <span className="modules-running">{runningModulesCount}</span>
            )}
            {installedModulesCount > 0 && runningModulesCount === 0 && (
              <span className="modules-count">{installedModulesCount}</span>
            )}
          </button>
        )}
        {onClipboardClick && (
          <button
            className="clipboard-btn"
            onClick={onClipboardClick}
            title="Universal Clipboard"
          >
            <span className="clipboard-icon">📋</span>
            {clipboardCount > 0 && (
              <span className="clipboard-count">{clipboardCount}</span>
            )}
          </button>
        )}
        {onMessagesClick && (
          <button
            className="messages-btn"
            onClick={onMessagesClick}
            title="Messages"
          >
            <span className="messages-icon">💬</span>
            {unreadMessagesCount > 0 && (
              <span className="messages-count">{unreadMessagesCount}</span>
            )}
          </button>
        )}
        {onMarkNotificationRead && onMarkAllNotificationsRead && onDeleteNotification && onClearAllNotifications && (
          <NotificationBell
            notifications={notifications}
            unreadCount={unreadNotificationCount}
            onMarkRead={onMarkNotificationRead}
            onMarkAllRead={onMarkAllNotificationsRead}
            onDelete={onDeleteNotification}
            onClearAll={onClearAllNotifications}
            onNotificationClick={onNotificationClick}
          />
        )}
        {onAccountSettings && (
          <button
            className="account-settings-btn"
            onClick={onAccountSettings}
            title="Account Settings"
          >
            ⚙️
          </button>
        )}
        {onThemeToggle && (
          <button
            className="theme-toggle-btn"
            onClick={onThemeToggle}
            title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>
        )}
        {onProfileEdit && (
          <button className="header-profile-btn" onClick={onProfileEdit} title="Edit profile">
            <Avatar
              src={profile?.avatarDataUrl}
              displayName={profile?.displayName || identityShortId}
              size="small"
            />
            <span className="header-profile-name">{profile?.displayName || identityShortId}</span>
            <span className="header-karma" title="Your karma">{karma >= 0 ? '+' : ''}{karma}</span>
          </button>
        )}
        {isRedPill && !onProfileEdit && (
          <span className="header-identity mono" title="Your identity">
            {identityShortId}
          </span>
        )}
      </div>
    </header>
  );
}
